import React, { useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

const ProjectsCards = () => {
	const [selectedProject, setSelectedProject] = useState(null);
	
	const data = useStaticQuery(graphql`
		query ProjectsWithMedia {
			allMarkdownRemark(
				filter: { fileAbsolutePath: { regex: "/content/storyboard/" } }
				sort: { fields: [frontmatter___title], order: [ASC] }
			) {
				nodes {
					id
					fileAbsolutePath
					frontmatter {
						title
						alt
						description
						videoLink
						media {
							file
							type
						}
					}
				}
			}
			allFile(filter: { relativeDirectory: { regex: "/media/storyboard/" } }) {
				nodes {
					id
					name
					sourceInstanceName
					relativePath
					extension
					publicURL
					childImageSharp {
						gatsbyImageData(width: 600, placeholder: BLURRED, formats: [AUTO, WEBP])
					}
				}
			}
		}
	`);

	const items = data?.allMarkdownRemark?.nodes || [];
	const files = data?.allFile?.nodes || [];

	// Build file lookup
	const fileByPath = {};
	files.forEach((f) => {
		fileByPath[f.publicURL] = f;
	});

	const renderMediaContent = (media) => {
		if (!media || !media.file) return null;

		const file = fileByPath[media.file];
		const mediaType = media.type || (file?.extension === "pdf" ? "pdf" : "image");

		switch (mediaType) {
			case "image":
				if (file?.childImageSharp) {
					return (
						<div style={{ width: "100%", height: "100%", overflow: "cover" }}>
							<GatsbyImage
								image={getImage(file)}
								alt="Project item"
								style={{ width: "100%", height: "100%", objectFit: "cover" }}
							/>
						</div>
					);
				}
				return (
					<img
						src={media.file}
						alt="project item"
						style={{ width: "50%", height: "50%", objectFit: "cover" }}
					/>
				);

			case "pdf":
				return (
					<div
						style={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							overflow: "hidden",
							position: "relative",
						}}
						onClick={() => window.open(media.file, "_blank")}
						role="button"
						tabIndex={0}
						onKeyPress={(e) => {
							if (e.key === "Enter") window.open(media.file, "_blank");
						}}
					>
						<iframe
							src={`${media.file}#toolbar=0`}
							style={{
								width: "100%",
								height: "100%",
								border: "none",
								pointerEvents: "none",
							}}
							title="PDF Preview"
						/>
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								opacity: 0,
								transition: "opacity 0.3s",
							}}
							className="pdf-overlay"
						>
							<span style={{ fontSize: "1.2rem", color: "#fff", fontWeight: "bold" }}>
								Click to open PDF
							</span>
						</div>
					</div>
				);

			case "video":
				return (
					<video
						style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#000" }}
						controls
						preload="metadata"
						poster={media.poster}
					>
						<source src={media.file} />
						Your browser does not support the video tag.
					</video>
				);

			default:
				return null;
		}
	};

	return (
		<>
			{/* Projects Grid */}
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "4rem",
					padding: "2rem",
					paddingTop: "100px",
					maxWidth: "1400px",
					margin: "0 auto",
				}}
			>
				{items.map((item, index) => {
					const isEven = index % 2 === 0;
					return (
						<div key={item.id} style={{ position: "relative" }}>
							{/* Large red angular background accent */}
							<div
								style={{
									position: "absolute",
									top: "-60px",
									[isEven ? "right" : "left"]: "-150px",
									width: "800px",
									height: "600px",
									background: "#810100",
									clipPath: isEven ? "polygon(30% 0%, 100% 0%, 100% 100%, 0% 70%)" : "polygon(0% 0%, 70% 0%, 100% 70%, 0% 100%)",
									zIndex: 0,
									opacity: 0.12,
								}}
							/>

							{/* Main card container - alternating layout */}
							<div
								style={{
									display: "grid",
									gridTemplateColumns: isEven ? "1.1fr 1fr" : "1fr 1.1fr",
									gap: "3rem",
									alignItems: "start",
									position: "relative",
									zIndex: 1,
								}}
							>
								{/* Image side (alternates left/right) */}
								<div style={{ order: isEven ? 0 : 1 }}>
									<div
										onClick={() => setSelectedProject(item)}
										style={{
											position: "relative",
											height: "500px",
											overflow: "hidden",
											border: "4px solid #810100",
											cursor: "pointer",
											backgroundColor: "#000",
											transition: "transform 0.3s ease",
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.transform = "scale(1.02)";
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.transform = "scale(1)";
										}}
									>
										<div style={{ width: "100%", height: "100%" }}>
											{renderMediaContent(item.frontmatter.media)}
										</div>
										{/* Click to see more overlay */}
										<div
											style={{
												position: "absolute",
												inset: 0,
												background: "rgba(0, 0, 0, 0.4)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												opacity: 0,
												transition: "opacity 0.3s ease",
											}}
											className="project-overlay"
										>
											<span style={{ color: "#fff", fontSize: "1.1rem", fontWeight: "bold" }}>
												Click to see more
											</span>
										</div>
										<style>{`
											div[onclick*="selectedProject"]:hover .project-overlay {
												opacity: 1;
											}
										`}</style>
									</div>
								</div>

								{/* Content side (alternates right/left) */}
								<div style={{ display: "flex", flexDirection: "column", gap: "2.5rem", paddingTop: "1rem", order: isEven ? 1 : 0 }}>
									<div>
										<h2
											style={{
												margin: "0 0 1.5rem 0",
												fontSize: "2.8rem",
												fontWeight: "900",
												color: "#edebdd",
												textTransform: "uppercase",
												letterSpacing: "0.15em",
												lineHeight: "1.1",
											}}
										>
											{item.frontmatter.title}
										</h2>
									</div>

									{item.frontmatter.description && (
										<div
											style={{
												paddingLeft: "1rem",
												borderLeft: "4px solid #810100",
												paddingTop: "1rem",
												paddingBottom: "1rem",
											}}
										>
											<p
												style={{
													margin: "0",
													fontSize: "0.95rem",
													color: "#edebdd",
													lineHeight: "1.8",
												}}
											>
												{item.frontmatter.description}
											</p>
										</div>
									)}

									{/* Video link button */}
									{item.frontmatter.videoLink && (
										<a
											href={item.frontmatter.videoLink}
											target="_blank"
											rel="noopener noreferrer"
											style={{
												display: "inline-block",
												padding: "12px 24px",
												backgroundColor: "#810100",
												color: "#edebdd",
												textDecoration: "none",
												fontWeight: "bold",
												textTransform: "uppercase",
												letterSpacing: "0.1em",
												transition: "all 0.3s ease",
												width: "fit-content",
											}}
											onMouseEnter={(e) => {
												e.currentTarget.style.backgroundColor = "#863000";
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.backgroundColor = "#810100";
											}}
										>
											View Video →
										</a>
									)}
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{/* Project Detail Modal */}
			{selectedProject && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						backgroundColor: "rgba(0, 0, 0, 0.95)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 9999,
						padding: "2rem",
					}}
					onClick={() => setSelectedProject(null)}
				>
					<div
						style={{
							backgroundColor: "#000",
							maxWidth: "1000px",
							maxHeight: "90vh",
							overflowY: "auto",
							padding: "3rem",
							position: "relative",
							border: "2px solid #863000",
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{/* Close button */}
						<button
							onClick={() => setSelectedProject(null)}
							style={{
								position: "absolute",
								top: "1.5rem",
								right: "1.5rem",
								background: "transparent",
								border: "none",
								fontSize: "2rem",
								color: "#863000",
								cursor: "pointer",
								width: "40px",
								height: "40px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								transition: "transform 0.2s ease",
							}}
							onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.2)"; }}
							onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
						>
							✕
						</button>

						{/* Modal header */}
						<div style={{ marginBottom: "2rem" }}>
							<h2
								style={{
									margin: "0 0 1rem 0",
									fontSize: "2.5rem",
									fontWeight: "900",
									color: "#fff",
									textTransform: "uppercase",
									letterSpacing: "0.15em",
								}}
							>
								{selectedProject.frontmatter.title}
							</h2>
						</div>

						{/* Featured image */}
						<div
							style={{
								marginBottom: "2rem",
								height: "400px",
								overflow: "hidden",
								border: "2px solid #863000",
								backgroundColor: "#000",
							}}
						>
							<div style={{ width: "100%", height: "100%" }}>
								{renderMediaContent(selectedProject.frontmatter.media)}
							</div>
						</div>

						{/* Description */}
						{selectedProject.frontmatter.description && (
							<div style={{ marginBottom: "2rem" }}>
								<h3
									style={{
										margin: "0 0 1rem 0",
										fontSize: "1.1rem",
										fontWeight: "700",
										color: "#810100",
										textTransform: "uppercase",
										letterSpacing: "0.1em",
									}}
								>
									Details
								</h3>
								<p
									style={{
										margin: "0",
										fontSize: "1rem",
										color: "#ddd",
										lineHeight: "1.9",
										whiteSpace: "pre-wrap",
									}}
								>
									{selectedProject.frontmatter.description}
								</p>
							</div>
						)}

						{/* Video link */}
						{selectedProject.frontmatter.videoLink && (
							<div style={{ marginTop: "2rem", paddingTop: "2rem", borderTop: "1px solid #444" }}>
								<a
									href={selectedProject.frontmatter.videoLink}
									target="_blank"
									rel="noopener noreferrer"
									style={{
										display: "inline-block",
										padding: "14px 32px",
										backgroundColor: "#701d17",
										color: "#fff",
										textDecoration: "none",
										fontWeight: "bold",
										textTransform: "uppercase",
										letterSpacing: "0.1em",
										fontSize: "1rem",
										transition: "all 0.3s ease",
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.backgroundColor = "#a01d15";
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = "#701d17";
									}}
								>
									Watch Full Video →
								</a>
							</div>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default ProjectsCards;